import { getSocket } from '../../socket/socket-client';

/**
 * Capa de servicio pura — sin React, sin JSX.
 *
 * Responsabilidades:
 *  - Suscribirse a los eventos del socket definidos en el registry.
 *  - Transformar el payload crudo usando config.transform().
 *  - Entregar la notificación normalizada a través del callback onNotify.
 *  - Reintentar la conexión si el socket aún no está disponible al montar.
 */
export class NotificationService {
  #registry   = {};
  #handlers   = new Map();
  #intervalId = null;
  #onNotify   = null;

  constructor(registry, onNotify) {
    this.#registry = registry;
    this.#onNotify = onNotify;
  }

  #buildHandler(eventName, config) {
    return (payload) => {
      if (config.silent) return; // suscripción activa, sin notificación visual
      const normalized = config.transform(payload);
      this.#onNotify({
        id:        `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        eventName,
        config,
        timestamp: new Date(),
        ...normalized,
      });
    };
  }

  #attach() {
    const sock = getSocket();
    if (!sock?.connected) return false;

    Object.entries(this.#registry).forEach(([eventName, config]) => {
      // Remover SOLO el handler previo del servicio, no todos los listeners del evento.
      // sock.off(eventName) sin handler destruiría los listeners externos (ej. useNotificationReload).
      const prev = this.#handlers.get(eventName);
      if (prev) sock.off(eventName, prev);

      const handler = this.#buildHandler(eventName, config);
      sock.on(eventName, handler);
      this.#handlers.set(eventName, handler);
    });

    return true;
  }

  start() {
    if (this.#attach()) return;

    this.#intervalId = setInterval(() => {
      if (this.#attach()) clearInterval(this.#intervalId);
    }, 2000);
  }

  stop() {
    clearInterval(this.#intervalId);

    const sock = getSocket();
    if (sock) {
      this.#handlers.forEach((handler, eventName) => {
        sock.off(eventName, handler);
      });
    }

    this.#handlers.clear();
  }
}
